import { useMemo, useState, type ReactNode } from 'react';
import './landing.css';

const CONTACT = {
  name: 'Oscar Silva Esquivel',
  phone: '(55) 492-100-27-40',
  phoneHref: 'tel:+52554921002740',
  whatsapp: '52554921002740',
  email: 'oscarsilva1770@icloud.com',
};

type IconName = 'arrow' | 'battery' | 'check' | 'drop' | 'home' | 'menu' | 'sun' | 'whatsapp' | 'x';

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    arrow: <><path d="M5 12h14" /><path d="m14 7 5 5-5 5" /></>,
    battery: <><rect x="3" y="6" width="17" height="12" rx="2" /><path d="M20 10h2v4h-2M7 10h5M9.5 8v4" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    drop: <path d="M12 3s6 6.3 6 11a6 6 0 0 1-12 0c0-4.7 6-11 6-11Z" />,
    home: <><path d="m3 11 9-8 9 8" /><path d="M5 10v10h14V10M9 20v-6h6v6" /></>,
    menu: <><path d="M4 7h16M4 12h16M4 17h16" /></>,
    sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.65 17.65l1.42 1.42M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.65 6.35l1.42-1.42" /></>,
    whatsapp: <><path d="M20 11.5a8 8 0 0 1-11.8 7L4 20l1.4-4.1A8 8 0 1 1 20 11.5Z" /><path d="M9 8.5c.4 2 2 3.6 4 4l1-1c.3-.3.7-.3 1-.1l1.5.8c.3.2.5.5.4.9-.3 1.2-1.4 2-2.7 1.9-3.6-.4-6.5-3.3-6.9-6.9C7.2 6.8 8 5.7 9.2 5.4c.4-.1.7.1.9.4l.8 1.5c.2.3.1.7-.1 1l-1 .9" /></>,
    x: <><path d="m6 6 12 12M18 6 6 18" /></>,
  };
  return <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{paths[name]}</svg>;
}

const services = [
  {
    number: '01',
    eyebrow: 'CASA Y NEGOCIO',
    title: 'Interconexión que empieza en tu recibo, no en un paquete.',
    description: 'Revisamos consumo, espacio y hábitos de uso para dimensionar un sistema que trabaje con la red eléctrica y pueda crecer contigo.',
    image: '/assets/servicio-interconexion.webp',
    alt: 'Paneles solares instalados en la azotea de una vivienda mexicana',
    icon: 'home' as IconName,
    bullets: ['Paneles de alta potencia', 'Inversor y protecciones', 'Monitoreo del sistema'],
  },
  {
    number: '02',
    eyebrow: 'CAMPO Y RANCHO',
    title: 'Agua donde la necesitas, impulsada directamente por el sol.',
    description: 'Diseñamos bombeo solar según profundidad, caudal, distancia y jornada de trabajo. La instalación se define después de conocer el pozo y el terreno.',
    image: '/assets/servicio-bombeo.webp',
    alt: 'Sistema de bombeo solar enviando agua a un canal de riego',
    icon: 'drop' as IconName,
    bullets: ['Bombas sumergibles', 'Controladores dedicados', 'Arreglo solar a la medida'],
  },
  {
    number: '03',
    eyebrow: 'ENERGÍA AUTÓNOMA',
    title: 'Respaldo para lo que no puede quedarse sin energía.',
    description: 'Integramos almacenamiento con baterías de litio para refrigeración, iluminación, conectividad y otras cargas esenciales, con prioridades claras.',
    image: '/assets/servicio-respaldo.webp',
    alt: 'Sistema solar autónomo con baterías y refrigeración para un negocio rural',
    icon: 'battery' as IconName,
    bullets: ['Baterías de litio', 'Controladores MPPT', 'Cargas verificadas antes de instalar'],
  },
];

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [lead, setLead] = useState({ name: '', location: '', service: 'Interconexión', need: '' });
  const whatsappHref = useMemo(() => {
    const message = [
      'Hola Legacy Solar, quiero solicitar una cotización.',
      lead.name && `Nombre: ${lead.name}`,
      lead.location && `Ubicación: ${lead.location}`,
      `Proyecto: ${lead.service}`,
      lead.need && `Necesidad: ${lead.need}`,
    ].filter(Boolean).join('\n');
    return `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(message)}`;
  }, [lead]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="landing-page">
      <header className="site-header">
        <a className="site-brand" href="#inicio" aria-label="Legacy Solar, inicio">
          <img src="/logo.jpeg" alt="" />
          <span><strong>LEGACY</strong><em>SOLAR</em></span>
        </a>
        <nav className={menuOpen ? 'site-nav is-open' : 'site-nav'} aria-label="Navegación principal">
          <a href="#soluciones" onClick={closeMenu}>Soluciones</a>
          <a href="#metodo" onClick={closeMenu}>Cómo trabajamos</a>
          <a href="#contacto" onClick={closeMenu}>Cotizar</a>
          <a className="nav-cta" href={whatsappHref} target="_blank" rel="noreferrer" onClick={closeMenu}>Hablar con Oscar <Icon name="arrow" /></a>
        </nav>
        <button className="menu-button" type="button" aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
          <Icon name={menuOpen ? 'x' : 'menu'} />
        </button>
      </header>

      <main>
        <section className="hero" id="inicio">
          <img className="hero-image" src="/assets/hero-campo-solar.webp" alt="Paneles solares alimentando riego en un campo mexicano al amanecer" fetchPriority="high" />
          <div className="hero-shade" />
          <div className="hero-content">
            <p className="hero-kicker"><span /> Energía solar para casa, negocio y campo</p>
            <h1>Energía que trabaja<br />donde tú <i>trabajas.</i></h1>
            <p className="hero-copy">Diseñamos sistemas solares a partir de lo que necesitas mover, conservar o producir. Sin paquetes a ciegas. Sin promesas infladas.</p>
            <div className="hero-actions">
              <a className="button button-primary" href="#contacto">Cuéntanos tu proyecto <Icon name="arrow" /></a>
              <a className="button button-ghost" href="#soluciones">Ver soluciones</a>
            </div>
          </div>
          <aside className="hero-note">
            <span>DIAGNÓSTICO PRIMERO</span>
            <p>Consumo. Cargas. Espacio. Distancia. Horas de operación.</p>
          </aside>
          <div className="hero-index" aria-hidden="true">LS / 01</div>
        </section>

        <section className="principle section-shell" aria-label="Nuestro enfoque">
          <p className="section-label">NO ES UN CATÁLOGO</p>
          <div className="principle-copy">
            <h2>No te vendemos un número de paneles.<br /><span>Calculamos el sistema que tu operación necesita.</span></h2>
            <p>Una casa, una bomba y una cámara de refrigeración pueden usar el mismo sol, pero no requieren la misma solución. Por eso cada propuesta comienza con preguntas concretas.</p>
          </div>
          <div className="principle-specs">
            <span>Paneles de alta potencia</span>
            <span>Baterías de litio</span>
            <span>Control MPPT</span>
            <span>Bombas sumergibles</span>
          </div>
        </section>

        <section className="services" id="soluciones">
          <div className="services-heading section-shell">
            <p className="section-label">TRES RUTAS DE SOLUCIÓN</p>
            <h2>Del techo al pozo.<br />Del recibo a la autonomía.</h2>
          </div>
          <div className="service-list">
            {services.map((service, index) => (
              <article className="service" key={service.number}>
                <figure>
                  <img src={service.image} alt={service.alt} loading="lazy" />
                  <figcaption>{service.number} / 03</figcaption>
                </figure>
                <div className="service-copy">
                  <span className="service-icon"><Icon name={service.icon} /></span>
                  <p className="service-eyebrow">{service.eyebrow}</p>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul>{service.bullets.map((bullet) => <li key={bullet}><Icon name="check" />{bullet}</li>)}</ul>
                  <a href="#contacto" onClick={() => setLead((current) => ({ ...current, service: index === 0 ? 'Interconexión' : index === 1 ? 'Bombeo solar' : 'Sistema autónomo' }))}>Quiero revisar mi proyecto <Icon name="arrow" /></a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="method" id="metodo">
          <div className="method-intro">
            <p className="section-label light">ANTES DE COTIZAR</p>
            <h2>Cuatro decisiones.<br /><i>Una propuesta clara.</i></h2>
            <p>El equipo se elige después de entender el trabajo. La cotización detalla alcance, componentes y condiciones para que sepas exactamente qué se instalará.</p>
          </div>
          <ol className="method-steps">
            <li><span>01</span><div><strong>Entendemos la carga</strong><p>Recibo de luz, equipos, potencia, horas de uso y prioridad.</p></div></li>
            <li><span>02</span><div><strong>Leemos el sitio</strong><p>Espacio, sombras, orientación, pozo, distancias y acceso.</p></div></li>
            <li><span>03</span><div><strong>Diseñamos el sistema</strong><p>Generación, control, almacenamiento y protecciones necesarias.</p></div></li>
            <li><span>04</span><div><strong>Cotizamos el alcance</strong><p>Equipos, instalación, tiempos y garantías especificados por escrito.</p></div></li>
          </ol>
        </section>

        <section className="use-cases section-shell">
          <div className="use-case-title">
            <p className="section-label">CARGAS QUE PODEMOS ESTUDIAR</p>
            <h2>Tu necesidad puede ser más concreta que “poner paneles”.</h2>
          </div>
          <div className="use-case-track">
            {['Refrigeración', 'Riego y bombeo', 'Iluminación', 'Cámaras y seguridad', 'Conectividad', 'Herramienta ligera', 'Vivienda completa'].map((item, index) => (
              <span key={item}><b>{String(index + 1).padStart(2, '0')}</b>{item}</span>
            ))}
          </div>
          <p className="use-case-note">Cada carga se valida antes de incluirla. Si el proyecto requiere obra, estructura o tendido especial, se cotiza como parte del alcance.</p>
        </section>

        <section className="contact" id="contacto">
          <div className="contact-copy">
            <p className="section-label light">EMPECEMOS CON LO ESENCIAL</p>
            <h2>¿Qué quieres hacer<br />con la energía del sol?</h2>
            <p>Déjanos cuatro datos. Con eso podemos preparar la primera conversación y decirte qué información falta para cotizar.</p>
            <div className="contact-person">
              <span>ATENCIÓN DIRECTA</span>
              <strong>{CONTACT.name}</strong>
              <a href={CONTACT.phoneHref}>{CONTACT.phone}</a>
              <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
            </div>
          </div>
          <form className="lead-form" onSubmit={(event) => event.preventDefault()}>
            <div className="field-row">
              <label><span>Tu nombre</span><input required value={lead.name} onChange={(event) => setLead({ ...lead, name: event.target.value })} placeholder="¿Cómo te llamas?" autoComplete="name" /></label>
              <label><span>Municipio o ciudad</span><input required value={lead.location} onChange={(event) => setLead({ ...lead, location: event.target.value })} placeholder="¿Dónde está el proyecto?" autoComplete="address-level2" /></label>
            </div>
            <fieldset>
              <legend>¿Qué necesitas?</legend>
              <div className="service-options">
                {['Interconexión', 'Bombeo solar', 'Sistema autónomo'].map((option) => (
                  <label className={lead.service === option ? 'selected' : ''} key={option}>
                    <input type="radio" name="service" value={option} checked={lead.service === option} onChange={() => setLead({ ...lead, service: option })} />
                    {option}
                  </label>
                ))}
              </div>
            </fieldset>
            <label><span>Cuéntanos qué quieres alimentar o resolver</span><textarea value={lead.need} onChange={(event) => setLead({ ...lead, need: event.target.value })} placeholder="Ej. una bomba para pozo, refrigeración para mi negocio o reducir el consumo de mi casa" rows={4} /></label>
            <a className="button form-submit" href={whatsappHref} target="_blank" rel="noreferrer"><Icon name="whatsapp" /> Solicitar diagnóstico por WhatsApp</a>
            <small>La información se prepara en tu dispositivo y se envía únicamente cuando abres WhatsApp.</small>
          </form>
        </section>

        <section className="faq section-shell">
          <div className="faq-heading"><p className="section-label">PREGUNTAS ÚTILES</p><h2>Antes de hablar,<br />esto conviene saber.</h2></div>
          <div className="faq-list">
            <details><summary>¿Todos los sistemas funcionan cuando se va la luz?<span>+</span></summary><p>No. Un sistema interconectado y uno con respaldo se comportan distinto. Si necesitas continuidad, hay que definir qué cargas son prioritarias y por cuánto tiempo.</p></details>
            <details><summary>¿Qué necesitan para preparar una cotización?<span>+</span></summary><p>Depende del proyecto: recibo eléctrico, lista de equipos, ubicación, fotografías del sitio y, para bombeo, profundidad, caudal esperado y distancia de conducción.</p></details>
            <details><summary>¿Puedo ampliar el sistema después?<span>+</span></summary><p>En muchos casos sí, siempre que se contemple desde el diseño. Lo revisamos desde el inicio para evitar reemplazar componentes antes de tiempo.</p></details>
            <details><summary>¿La instalación siempre está incluida?<span>+</span></summary><p>El alcance se presenta por escrito. Obra, estructura, cableado extendido o adecuaciones especiales se revisan según las condiciones reales del sitio.</p></details>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-brand"><img src="/logo.jpeg" alt="Legacy Solar" /><p>Soluciones solares diseñadas alrededor del trabajo real.</p></div>
        <div><span>CONTACTO</span><a href={CONTACT.phoneHref}>{CONTACT.phone}</a><a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></div>
        <div><span>COBERTURA</span><p>CDMX · Estado de México · Hidalgo · Querétaro</p><small>Proyectos foráneos bajo programación.</small></div>
        <p className="footer-legal">© 2026 Legacy Solar · Proyecto y condiciones sujetos a diagnóstico técnico.</p>
      </footer>

      <a className="floating-whatsapp" href={whatsappHref} target="_blank" rel="noreferrer" aria-label="Cotizar por WhatsApp"><Icon name="whatsapp" /></a>
    </div>
  );
}
